import { TokenIcon } from "@/components/brand/TokenIcon";
import { useGetPublicPriceFeeds } from "@/hooks/usePublicProtocol";
import { useAuth } from "@/lib/auth";
import { ROUTES } from "@/lib/routes";
import { cn, formatNum } from "@/lib/utils";
import { ChevronDown } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";

const ORDER = ["USDM", "ckBTC", "ckETH", "ICP", "MMK", "ckUSDC"] as const;

/**
 * Compact total ≈ USD chip (h-10, matches Search/Wallet); expands to wide
 * per-asset rows: icon · TICKER · AMOUNT (primary green) · ≈ $USD.
 */
export function WalletBalanceMenu() {
	const { balances, balancesLive, isAuthenticated } = useAuth();
	const { data: priceData } = useGetPublicPriceFeeds();
	const [open, setOpen] = useState(false);
	const btnRef = useRef<HTMLButtonElement>(null);
	const [pos, setPos] = useState({ top: 0, right: 0 });
	const location = useLocation();
	const prices = priceData?.prices ?? { ICP: 0, ckBTC: 0, ckETH: 0 };

	const rows = useMemo(() => {
		const amount: Record<(typeof ORDER)[number], number> = {
			USDM: balances.USDM,
			ckBTC: balances.ckBTC,
			ckETH: balances.ckETH,
			ICP: balances.ICP,
			MMK: balances.MMK,
			ckUSDC: balances.ckUSDC ?? 0,
		};
		return ORDER.map((id) => {
			const amt = amount[id];
			const px =
				id === "USDM" || id === "ckUSDC"
					? 1
					: id === "MMK"
						? 0
						: (prices[id as "ICP" | "ckBTC" | "ckETH"] ?? 0);
			const equiv = id === "MMK" ? null : amt * px;
			return { id, amt, equiv };
		});
	}, [balances, prices]);

	const totalUsdm = useMemo(() => {
		return rows.reduce((s, r) => s + (r.equiv ?? 0), 0);
	}, [rows]);

	useEffect(() => {
		setOpen(false);
	}, [location.pathname]);

	useEffect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setOpen(false);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open]);

	function openMenu() {
		const el = btnRef.current;
		if (el) {
			const r = el.getBoundingClientRect();
			setPos({
				top: r.bottom + 8,
				right: Math.max(8, window.innerWidth - r.right),
			});
		}
		setOpen((v) => !v);
	}

	if (!isAuthenticated) return null;

	const panel =
		open && typeof document !== "undefined"
			? createPortal(
					<>
						<button
							type="button"
							className="fixed inset-0 z-[199] cursor-default bg-transparent"
							aria-label="Close balances"
							onClick={() => setOpen(false)}
						/>
						<div
							className="fixed z-[200] w-[min(28rem,calc(100vw-1rem))] min-w-[22rem] rounded-xl border border-border bg-card p-3 shadow-lift"
							style={{ top: pos.top, right: pos.right }}
							role="menu"
						>
							<p className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
								Wallet · ticker · amount · ≈ USD
							</p>
							{/* No max-height scroll — six tokens fit without scrollbar */}
							<ul className="space-y-0.5">
								{rows.map((r) => (
									<li
										key={r.id}
										className="grid grid-cols-[auto_minmax(3.5rem,1fr)_auto_auto] items-center gap-x-3 rounded-lg px-2 py-2.5 text-sm hover:bg-muted/50"
									>
										<TokenIcon token={r.id} size="sm" plain />
										<span className="font-semibold tracking-wide">
											{r.id}
										</span>
										<span className="font-mono-amount text-right text-sm font-bold tabular-nums text-primary">
											{formatNum(
												r.amt,
												r.id === "ckBTC" ? 4 : r.id === "MMK" ? 0 : 2,
											)}
										</span>
										<span className="min-w-[4.5rem] text-right font-mono text-xs tabular-nums text-muted-foreground">
											{r.equiv != null
												? `≈ $${formatNum(r.equiv, 0)}`
												: "—"}
										</span>
									</li>
								))}
							</ul>
							<Link
								to={ROUTES.wallet}
								className="mt-2 flex w-full items-center justify-center rounded-lg border border-border py-2.5 text-xs font-bold text-primary hover:bg-muted/40"
								onClick={() => setOpen(false)}
							>
								Open wallet
							</Link>
						</div>
					</>,
					document.body,
				)
			: null;

	return (
		<div className="relative hidden sm:block">
			<button
				ref={btnRef}
				type="button"
				onClick={openMenu}
				className={cn(
					/* Match Search / Wallet: h-10 rounded-xl border */
					"inline-flex h-10 items-center gap-1.5 rounded-xl border border-border bg-card px-2.5 text-sm font-semibold hover:border-primary/40 sm:px-3",
					open && "border-primary/50",
				)}
				title={
					balancesLive
						? "Total wallet value at oracle prices (click for assets)"
						: "Balances loading…"
				}
				aria-expanded={open}
				aria-label={`Total wallet value about ${formatNum(totalUsdm, 0)} US dollars`}
			>
				<span className="font-mono-amount text-sm font-bold tabular-nums text-primary">
					≈ ${formatNum(totalUsdm, 0)}
				</span>
				<ChevronDown
					className={cn(
						"size-4 shrink-0 text-primary/70 transition-transform duration-200",
						open && "rotate-180",
					)}
				/>
			</button>
			{panel}
		</div>
	);
}
